import { clsx } from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';

type Tone = 'neutral' | 'coral' | 'green' | 'navy' | 'muted' | 'warning';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  selected?: boolean;
  children: ReactNode;
}

const toneClass: Record<Tone, string> = {
  neutral: 'border-hairline text-ink',
  coral: 'border-coral text-coral',
  green: 'border-deep-enterprise-green text-deep-enterprise-green',
  navy: 'border-dark-navy text-dark-navy',
  muted: 'border-border-light text-muted-slate',
  warning: 'border-error-red text-error-red',
};

const selectedTone: Record<Tone, string> = {
  neutral: 'bg-near-black text-canvas-white border-near-black',
  coral: 'bg-coral text-canvas-white border-coral',
  green: 'bg-deep-enterprise-green text-canvas-white border-deep-enterprise-green',
  navy: 'bg-dark-navy text-canvas-white border-dark-navy',
  muted: 'bg-muted-slate text-canvas-white border-muted-slate',
  warning: 'bg-error-red text-canvas-white border-error-red',
};

export function Chip({ tone = 'neutral', selected, className, children, ...rest }: ChipProps) {
  return (
    <span
      {...rest}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-pill border px-3 py-1 text-caption transition-colors',
        selected ? selectedTone[tone] : toneClass[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

export function AreaChip({ area, selected, onClick, className }: { area: string; selected?: boolean; onClick?: () => void; className?: string }) {
  const tone: Tone = ['Work', 'Personal', 'Health', 'Social'].includes(area)
    ? 'neutral'
    : 'coral';
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-pill border px-3 py-1 text-caption transition-all',
        selected ? selectedTone[tone] : `${toneClass[tone]} hover:bg-soft-stone`,
        onClick && 'cursor-pointer',
        className
      )}
    >
      {area}
    </button>
  );
}
